import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CreateButton from './CreateButton';
import styles from './CategoriesTabContent.module.css';
import '../styles/variables.css';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import {
    loadCategories,
    addCategory,
    editCategory,
    removeCategory,
    addSubcategory,
    editSubcategory,
    removeSubcategory,
    selectCategories,
    selectCategoriesLoading,
    selectCategoriesError,
    selectOperationLoading,
    selectOperationError,
    clearError,
    clearOperationError
} from '../store/slices/categoriesSlice';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';

const CategoriesTabContent = () => {
    const dispatch = useDispatch();

    // Get categories permissions
    const { hasAddCategory, hasDeleteCategory, hasUpdateCategory } = useCachedPermissions();

    const categories = useSelector(selectCategories);
    const loading = useSelector(selectCategoriesLoading);
    const error = useSelector(selectCategoriesError);
    const operationLoading = useSelector(selectOperationLoading);
    const operationError = useSelector(selectOperationError);

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [showAddSubcategory, setShowAddSubcategory] = useState(false);
    const [showEditCategory, setShowEditCategory] = useState(false);
    const [showEditSubcategory, setShowEditSubcategory] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    useEffect(() => {
        dispatch(loadCategories());
    }, [dispatch]);

    useEffect(() => {
        if (!showAddCategory && !showEditCategory) {
            dispatch(clearOperationError());
        }
    }, [showAddCategory, showEditCategory, dispatch]);

    const handleAddCategory = () => {
        if (!hasAddCategory) {
            dispatch(showErrorNotification('You do not have permission to add categories'));
            return;
        }
        setShowAddCategory(true);
        setFormData({ name: '', description: '' });
    };

    const handleAddSubcategory = (categoryId) => {
        setSelectedCategory(categories.find(cat => (cat._id || cat.id) === categoryId));
        setShowAddSubcategory(true);
        setFormData({ name: '' });
    };

    const handleEditCategory = (category) => {
        if (!hasUpdateCategory) {
            dispatch(showErrorNotification('You do not have permission to update categories'));
            return;
        }
        setSelectedCategory(category);
        setShowEditCategory(true);
        setFormData({ name: category.name, description: category.description });
    };

    const handleEditSubcategory = (subcategory, category) => {
        if (!hasUpdateCategory) {
            dispatch(showErrorNotification('You do not have permission to update categories'));
            return;
        }
        setSelectedSubcategory(subcategory);
        setSelectedCategory(category);
        setShowEditSubcategory(true);
        setFormData({ name: subcategory.name });
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!hasDeleteCategory) {
            dispatch(showErrorNotification('You do not have permission to delete categories'));
            return;
        }
        if (window.confirm('Are you sure you want to delete this category? This will also delete all its subcategories.')) {
            try {
                await dispatch(removeCategory(categoryId)).unwrap();
                dispatch(showSuccessNotification('Category deleted successfully!'));
            } catch (err) {
                console.error('Error deleting category:', err);
                dispatch(showErrorNotification(`Failed to delete category: ${err}`));
            }
        }
    };

    const handleDeleteSubcategory = async (categoryId, subcategoryId) => {
        if (!hasDeleteCategory) {
            dispatch(showErrorNotification('You do not have permission to delete categories'));
            return;
        }
        if (window.confirm('Are you sure you want to delete this subcategory?')) {
            try {
                await dispatch(removeSubcategory(subcategoryId)).unwrap();
                dispatch(showSuccessNotification('Subcategory deleted successfully!'));
            } catch (err) {
                console.error('Error deleting subcategory:', err);
                dispatch(showErrorNotification(`Failed to delete subcategory: ${err}`));
            }
        }
    };

    const handleSubmitCategory = async (e) => {
        e.preventDefault();
        try {
            if (showAddCategory) {
                const newCategoryData = {
                    name: formData.name,
                    description: formData.description
                };
                await dispatch(addCategory(newCategoryData)).unwrap();
            } else if (showEditCategory) {
                const updateData = {
                    name: formData.name,
                    description: formData.description
                };
                console.log('Selected category for edit:', selectedCategory);
                const categoryId = selectedCategory._id || selectedCategory.id;
                console.log('Using category ID:', categoryId);
                await dispatch(editCategory({
                    categoryId: categoryId,
                    categoryData: updateData
                })).unwrap();
            }
            setShowAddCategory(false);
            setShowEditCategory(false);
            setFormData({ name: '', description: '' });
            dispatch(showSuccessNotification(showAddCategory ? 'Category created successfully!' : 'Category updated successfully!'));
        } catch (err) {
            console.error('Error saving category:', err);
            dispatch(showErrorNotification(`Failed to save category: ${err}`));
        }
    };

    const handleSubmitSubcategory = async (e) => {
        e.preventDefault();
        try {
            if (showAddSubcategory) {
                const subcategoryData = {
                    name: formData.name,
                    category_id: selectedCategory._id || selectedCategory.id
                };
                await dispatch(addSubcategory(subcategoryData)).unwrap();
            } else if (showEditSubcategory) {
                const subcategoryData = {
                    name: formData.name,
                    category_id: selectedCategory._id || selectedCategory.id
                };
                await dispatch(editSubcategory({
                    subcategoryId: selectedSubcategory._id || selectedSubcategory.id,
                    subcategoryData: subcategoryData
                })).unwrap();
            }
            setShowAddSubcategory(false);
            setShowEditSubcategory(false);
            setFormData({ name: '', description: '' });
            dispatch(showSuccessNotification(showAddSubcategory ? 'Subcategory created successfully!' : 'Subcategory updated successfully!'));
        } catch (err) {
            console.error('Error saving subcategory:', err);
            dispatch(showErrorNotification(`Failed to save subcategory: ${err}`));
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Show loading state
    if (loading) {
        return (
            <div className={styles.categoriesContainer}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Categories Management</h2>
                    {hasAddCategory && (
                        <button className={styles.addCategoryButton} onClick={handleAddCategory}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Add Category
                        </button>
                    )}
                </div>
                <div className={styles.loadingState}>
                    <div className={styles.loadingSpinner}></div>
                    <p>Loading categories...</p>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className={styles.categoriesContainer}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Categories Management</h2>
                    {hasAddCategory && (
                        <button className={styles.addCategoryButton} onClick={handleAddCategory}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Add Category
                        </button>
                    )}
                </div>
                <div className={styles.errorState}>
                    <p className={styles.errorMessage}>{error}</p>
                    <button className={styles.retryButton} onClick={() => dispatch(loadCategories())}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.categoriesContainer}>
            <div className={styles.header}>
                <h2 className={styles.title}>Categories Management</h2>
                {hasAddCategory && (
                    <button className={styles.addCategoryButton} onClick={handleAddCategory}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        Add Category
                    </button>
                )}
            </div>

            {!Array.isArray(categories) || categories.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No categories found. Create your first category to get started.</p>
                </div>
            ) : (
                <div className={styles.categoriesGrid}>
                    {categories.map((category) => (
                        <div key={category._id || category.id} className={styles.categoryCard}>
                            <div className={styles.categoryHeader}>
                                <div className={styles.categoryInfo}>
                                    <h3 className={styles.categoryName}>{category.name}</h3>
                                    <p className={styles.categoryDescription}>{category.description}</p>
                                    <p className={styles.categoryDate}>Created: {formatDate(category.createdAt)}</p>
                                </div>
                                <div className={styles.categoryActions}>
                                    {hasUpdateCategory && (
                                        <button
                                            className={styles.actionButton}
                                            onClick={() => handleEditCategory(category)}
                                        >
                                            Edit
                                        </button>
                                    )}
                                    {hasDeleteCategory && (
                                        <button
                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                            onClick={() => handleDeleteCategory(category._id || category.id)}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className={styles.subcategoriesSection}>
                                <div className={styles.subcategoriesHeader}>
                                    <h4 className={styles.subcategoriesTitle}>Subcategories ({category.subcategories?.length || 0})</h4>
                                    {hasAddCategory && (
                                        <button
                                            className={styles.addSubcategoryButton}
                                            onClick={() => handleAddSubcategory(category._id || category.id)}
                                        >
                                            + Add Subcategory
                                        </button>
                                    )}
                                </div>

                                <div className={styles.subcategoriesList}>
                                    {category.subcategories?.map((subcategory) => (
                                        <div key={subcategory.id} className={styles.subcategoryItem}>
                                            <div className={styles.subcategoryInfo}>
                                                <h5 className={styles.subcategoryName}>{subcategory.name}</h5>
                                                <p className={styles.subcategoryDescription}>{subcategory.description}</p>
                                            </div>
                                            <div className={styles.subcategoryActions}>
                                                {hasUpdateCategory && (
                                                    <button
                                                        className={styles.actionButton}
                                                        onClick={() => handleEditSubcategory(subcategory, category)}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                {hasDeleteCategory && (
                                                    <button
                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                        onClick={() => handleDeleteSubcategory(category._id || category.id, subcategory._id || subcategory.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {(!category.subcategories || category.subcategories.length === 0) && (
                                        <p className={styles.noSubcategories}>No subcategories yet</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Category Modal */}
            {showAddCategory && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>Add New Category</h3>
                        <form onSubmit={handleSubmitCategory}>
                            <div className={styles.formGroup}>
                                <label>Name:</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Description:</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    required
                                />
                            </div>
                            {operationError && (
                                <div className={styles.errorMessage}>
                                    {operationError}
                                </div>
                            )}
                            <div className={styles.modalActions}>
                                <button type="submit" disabled={operationLoading}>
                                    {operationLoading ? 'Adding...' : 'Add Category'}
                                </button>
                                <button type="button" onClick={() => setShowAddCategory(false)} disabled={operationLoading}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Category Modal */}
            {showEditCategory && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>Edit Category</h3>
                        <form onSubmit={handleSubmitCategory}>
                            <div className={styles.formGroup}>
                                <label>Name:</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Description:</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    required
                                />
                            </div>
                            {operationError && (
                                <div className={styles.errorMessage}>
                                    {operationError}
                                </div>
                            )}
                            <div className={styles.modalActions}>
                                <button type="submit" disabled={operationLoading}>
                                    {operationLoading ? 'Updating...' : 'Update Category'}
                                </button>
                                <button type="button" onClick={() => setShowEditCategory(false)} disabled={operationLoading}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Subcategory Modal */}
            {showAddSubcategory && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>Add New Subcategory to {selectedCategory?.name}</h3>
                        <form onSubmit={handleSubmitSubcategory}>
                            <div className={styles.formGroup}>
                                <label>Name:</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            {operationError && (
                                <div className={styles.errorMessage}>
                                    {operationError}
                                </div>
                            )}
                            <div className={styles.modalActions}>
                                <button type="submit" disabled={operationLoading}>
                                    {operationLoading ? 'Adding...' : 'Add Subcategory'}
                                </button>
                                <button type="button" onClick={() => setShowAddSubcategory(false)} disabled={operationLoading}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Subcategory Modal */}
            {showEditSubcategory && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>Edit Subcategory</h3>
                        <form onSubmit={handleSubmitSubcategory}>
                            <div className={styles.formGroup}>
                                <label>Name:</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>
                            {operationError && (
                                <div className={styles.errorMessage}>
                                    {operationError}
                                </div>
                            )}
                            <div className={styles.modalActions}>
                                <button type="submit" disabled={operationLoading}>
                                    {operationLoading ? 'Updating...' : 'Update Subcategory'}
                                </button>
                                <button type="button" onClick={() => setShowEditSubcategory(false)} disabled={operationLoading}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoriesTabContent; 